import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Web as WebIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSPage } from '../../../services';

const CMSPages: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pages, setPages] = useState<CMSPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [createDialog, setCreateDialog] = useState(false);
  const [newPageData, setNewPageData] = useState({ slug: '', titulo: '' });
  const [saving, setSaving] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; page: CMSPage | null }>({
    open: false,
    page: null,
  });

  const fetchPages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cmsService.getPages();
      setPages(response.data.data || []);
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingPages'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleCreatePage = async () => {
    if (!newPageData.titulo || !newPageData.slug) return;
    setSaving(true);
    setError(null);
    try {
      const response = await cmsService.createPage({
        slug: newPageData.slug,
        titulo: newPageData.titulo,
        status: 'rascunho',
      });
      setSuccess(t('cms.pageCreated'));
      setCreateDialog(false);
      setNewPageData({ slug: '', titulo: '' });
      // Navigate to edit the new page
      navigate(`/cms/pages/${response.data.data.id}`);
    } catch (err: any) {
      setError(err.message || t('cms.errorCreatingPage'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.page) return;
    try {
      await cmsService.deletePage(deleteDialog.page.id);
      setSuccess(t('cms.pageDeleted'));
      setDeleteDialog({ open: false, page: null });
      fetchPages();
    } catch (err: any) {
      setError(err.message || t('cms.errorDeletingPage'));
    }
  };

  const filteredPages = pages.filter(page => {
    if (!searchTerm) return true;
    return (
      page.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      page.slug.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            {t('cms.pagesTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('cms.pagesSubtitle')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialog(true)}
        >
          {t('cms.newPage')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t('cms.searchPages')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('cms.page')}</TableCell>
                <TableCell>{t('cms.slug')}</TableCell>
                <TableCell>{t('cms.features')}</TableCell>
                <TableCell>{t('cms.faq')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('cms.lastUpdate')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredPages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <WebIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">
                      {t('cms.noPageFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPages.map((page) => (
                  <TableRow key={page.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {page.titulo}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        /{page.slug}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={page.total_features || 0}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={page.total_faq || 0}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={page.status === 'publicado' ? 'success' : 'default'}
                        label={page.status === 'publicado' ? t('cms.published') : t('cms.draft')}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {page.updated_at
                          ? new Date(page.updated_at).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })
                          : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('cms.editContent')}>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/cms/pages/${page.id}`)}
                        >
                          <SettingsIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, page })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('cms.newPage')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('cms.titleLabel')}
            value={newPageData.titulo}
            onChange={(e) => {
              const titulo = e.target.value;
              setNewPageData({
                titulo,
                slug: generateSlug(titulo),
              });
            }}
            margin="normal"
            required
            placeholder={t('cms.titlePlaceholderPage')}
          />
          <TextField
            fullWidth
            label={t('cms.slugUrl')}
            value={newPageData.slug}
            onChange={(e) => setNewPageData(prev => ({ ...prev, slug: e.target.value }))}
            margin="normal"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">/</InputAdornment>
              ),
            }}
            helperText={t('cms.slugHelperText')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleCreatePage}
            variant="contained"
            disabled={saving || !newPageData.titulo || !newPageData.slug}
          >
            {saving ? t('common.creating') : t('cms.createPage')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, page: null })}
      >
        <DialogTitle>{t('cms.deletePage')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('cms.confirmDeletePage', { name: deleteDialog.page?.titulo })}
          </Typography>
          <Typography variant="body2" color="error" mt={1}>
            {t('cms.deletePageWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, page: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CMSPages;
